import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";

import api, { formatApiErrorDetail } from "@/lib/api";
import { ModuleTitleER } from "@/components/er/ModuleTitleER";

export default function ErNoticeEditor() {
  const [text, setText] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/er/notice");
      setText(data.text || "");
      setUpdatedAt(data.updated_at);
      setUpdatedBy(data.updated_by_name);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!text.trim()) {
      toast.error("Notice text is required.");
      return;
    }
    setBusy(true);
    try {
      await api.put("/er/notice", { text: text.trim() });
      toast.success("Notice updated.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="er-notice-editor-page">
      <div>
        <ModuleTitleER />
        <h1 className="mt-3 font-serif text-forest-900 text-4xl">Edit Notice Text</h1>
        <p className="mt-2 text-charcoal-soft">
          This block appears prominently on the ER Center landing page. Keep the tone warm,
          plain-language, and safety-forward. Every edit is audit-logged.
        </p>
      </div>

      {updatedAt && (
        <p className="text-xs text-charcoal-muted">
          Last updated {new Date(updatedAt).toLocaleString()}
          {updatedBy ? ` by ${updatedBy}` : ""}
        </p>
      )}

      <section className="patria-card p-6" data-testid="er-notice-form">
        <label className="patria-label" htmlFor="nb">Notice text *</label>
        <textarea
          id="nb"
          className="patria-input mt-2 font-serif leading-relaxed w-full min-h-64"
          rows={12}
          value={text}
          onChange={(e) => setText(e.target.value)}
          data-testid="er-notice-text"
        />
        <div className="mt-4 flex justify-end">
          <button
            onClick={save}
            disabled={busy || !text.trim()}
            className="patria-btn-primary"
            data-testid="er-notice-save"
          >
            <Save className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
            {busy ? "Saving…" : "Save notice"}
          </button>
        </div>
      </section>
    </div>
  );
}
